import { Download, GitFork, Languages, Moon, RotateCcw, RotateCw, Sun, Trash2 } from 'lucide-react';
import type { AppTheme } from '../domain/types';
import type { TranslationKey } from '../i18n';
import { IconButton } from './IconButton';
import ui from './ui.module.css';
import styles from './Toolbar.module.css';

interface ToolbarProps {
  filename: string;
  theme: AppTheme;
  errors: number;
  warnings: number;
  canUndo: boolean;
  canRedo: boolean;
  t: (key: TranslationKey, values?: Record<string, string | number>) => string;
  onUndo: () => void;
  onRedo: () => void;
  onTheme: () => void;
  onLanguage: () => void;
  onReset: () => void;
  onExport: () => void;
}

export function Toolbar({
  filename,
  theme,
  errors,
  warnings,
  canUndo,
  canRedo,
  t,
  onUndo,
  onRedo,
  onTheme,
  onLanguage,
  onReset,
  onExport,
}: ToolbarProps) {
  const statusText =
    errors > 0
      ? t(errors === 1 ? 'error' : 'errors', { count: errors })
      : warnings > 0
        ? t(warnings === 1 ? 'warning' : 'warnings', { count: warnings })
        : t('ready');

  return (
    <header className={styles.toolbar}>
      <div className={styles.brand}>
        <img src="./logo.svg" width="30" height="30" alt="" aria-hidden="true" />
        <div>
          <strong>{t('appName')}</strong>
          <span>{filename}</span>
        </div>
      </div>

      <div
        className={styles.status}
        data-status={errors > 0 ? 'error' : warnings > 0 ? 'warning' : 'ready'}
        data-testid="validation-status"
        role="status"
      >
        <span aria-hidden="true" />
        {statusText}
        {errors > 0 && warnings > 0 ? (
          <small>{t(warnings === 1 ? 'warning' : 'warnings', { count: warnings })}</small>
        ) : null}
      </div>

      <div className={styles.actions}>
        <div className={styles.actionGroup}>
          <IconButton icon={RotateCcw} label={t('undo')} disabled={!canUndo} onClick={onUndo} />
          <IconButton icon={RotateCw} label={t('redo')} disabled={!canRedo} onClick={onRedo} />
        </div>
        <div className={styles.actionGroup}>
          <IconButton
            icon={theme === 'dark' ? Sun : Moon}
            label={theme === 'dark' ? t('lightTheme') : t('darkTheme')}
            data-testid="theme-toggle"
            onClick={onTheme}
          />
          <IconButton
            icon={Languages}
            label={t('switchLanguage')}
            data-testid="lang-toggle"
            onClick={onLanguage}
          />
          <a
            className={styles.iconLink}
            href="https://github.com/KanadeK/issue-mint"
            target="_blank"
            rel="noreferrer"
            aria-label={t('repository')}
            title={t('repository')}
          >
            <GitFork size={16} strokeWidth={1.8} aria-hidden="true" />
          </a>
          <IconButton icon={Trash2} label={t('reset')} data-testid="reset" onClick={onReset} />
        </div>
        <button
          type="button"
          className={ui.primaryButton}
          data-testid="export-open"
          onClick={onExport}
        >
          <Download size={15} strokeWidth={1.8} aria-hidden="true" />
          <span>{t('export')}</span>
        </button>
      </div>
    </header>
  );
}
